import { IDataEntry, IDataSet } from 'components/Graphs/types/types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import 'dayjs/locale/pt-br'

export const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
dayjs.locale('pt-br')
dayjs.extend(utc)
dayjs.extend(timezone)

const now = dayjs().tz(timeZone)

/**
 * Process the data entries into time intervals.
 * @param data - The data entries to process.
 * @param intervalCount - The number of intervals to create.
 * @param intervalSizeMs - The size of each interval in milliseconds.
 * @param formatter - A function to format the label for each interval.
 * @param nowDate - The current date to use for calculations.
 * @returns An array of data sets for each time interval.
 */
const processTimeIntervals = (
  data: IDataEntry[],
  intervalCount: number,
  intervalSizeMs: number,
  formatter: (index: number) => string,
  nowDate: dayjs.Dayjs
): IDataSet[] => {
  if (data.length === 0) return []

  const result: IDataSet[] = []

  const lastIndex = intervalCount - 1

  for (let i = lastIndex; i >= 0; i--) {
    const dateOfLabel = nowDate.subtract(intervalSizeMs * i, 'milliseconds')
    result[lastIndex - i] = {
      label: formatter(dateOfLabel.valueOf()),
      sum: 0
    }
  }

  // Assign data to the correct interval
  data.forEach(entry => {
    const createdAt = dayjs(entry.created_at).tz(timeZone)

    const diff = nowDate.valueOf() - createdAt.valueOf()
    const div = diff / intervalSizeMs
    const valueIndex = Math.floor(intervalCount - div)

    if (valueIndex >= 0 && valueIndex < intervalCount) {
      if (!Number.isNaN(result[valueIndex]?.sum ?? 0)) {
        result[valueIndex].sum! += entry.value
      }
    }
  })

  return result
}

const getHourOffsetFromUTC = (): number => {
  const saoPauloNow = dayjs()
    .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
    .startOf('hour')
  return saoPauloNow.utcOffset() / 60 // retorna -3
}

/**
 * Group data by the last 24 hours (split hour by hour).
 * @param data - The data entries to process.
 * @param refDate - The reference date to use for grouping.
 * @returns An array of data sets for each hour.
 */
export const groupLast24Hours = (
  data: IDataEntry[],
  refDate: dayjs.Dayjs = now
) => {
  return processTimeIntervals(
    data,
    24,
    60 * 60 * 1000,
    date => dayjs(date).format('HH[h]'),
    refDate
  )
}

/**
 * Group data by last 7 days (split day by day).
 * @param data - The data entries to process.
 * @param refDate - The reference date to use for grouping.
 * @returns An array of data sets for each day.
 */
export const groupLast7Days = (
  data: IDataEntry[],
  refDate: dayjs.Dayjs = now
) => {
  return processTimeIntervals(
    data,
    7,
    24 * 60 * 60 * 1000,
    date => dayjs(date).format('DD/MM (ddd)'),
    refDate
  )
}

/**
 * Group data by last 30 days (split day by day).
 * @param data - The data entries to process.
 * @param refDate - The reference date to use for grouping.
 * @returns An array of data sets for each day.
 */
export const groupLast30Days = (
  data: IDataEntry[],
  refDate: dayjs.Dayjs = now
) => {
  return processTimeIntervals(
    data,
    30,
    24 * 60 * 60 * 1000,
    date => dayjs(date).format('DD/MM'),
    refDate
  )
}

/**
 * Group data by last 12 months (split month by month).
 * @param data - The data entries to process.
 * @param refDate - The reference date to use for grouping.
 * @returns An array of data sets for each month.
 */
export const groupLast12Months = (
  data: IDataEntry[],
  refDate: dayjs.Dayjs = now
): IDataSet[] => {
  // Passo 1: Agrupar valores por dia
  const dailyValues: Record<string, number[]> = {}

  data.forEach(entry => {
    const dateKey = dayjs(entry.created_at).format('YYYY-MM-DD') // Chave única para cada dia

    if (!dailyValues[dateKey]) {
      dailyValues[dateKey] = []
    }

    dailyValues[dateKey].push(entry.value)
  })

  // Passo 2: Selecionar o maior valor de cada dia
  const dailyMaxValues: Record<string, number> = {}
  for (const dateKey in dailyValues) {
    dailyMaxValues[dateKey] = Math.max(...dailyValues[dateKey])
  }

  // Passo 3: Agrupar por mês e calcular a média mensal
  const grouped: number[][] = Array.from({ length: 12 }, () => [])

  for (const dateKey in dailyMaxValues) {
    const diffMonths = dayjs(refDate).diff(dayjs(dateKey), 'month')

    if (diffMonths >= 0 && diffMonths < 12) {
      grouped[11 - diffMonths].push(dailyMaxValues[dateKey])
    }
  }

  // Passo 4: Calcular a média mensal
  return grouped.map((values, index) => {
    const date = dayjs(refDate).subtract(11 - index, 'month')
    const sum = values.reduce((a, b) => a + Number(b), 0)
    return {
      label: date.format('MMM'),
      sum: values.length > 0 ? Number(sum.toFixed(0)) : null,
      avg: values.length > 0 ? Number((sum / values.length).toFixed(0)) : null
    }
  })
}
